// The tenant's own artwork is served from the CDN bucket. A tenant that has
// never uploaded a logo — and any build without `REACT_APP_CDN_DOMAIN` — used
// to render `undefined/company/logo.png`, so the corner of the app showed the
// `<img>` alt text and the brand read as a bare word instead of a logo. Every
// logo therefore resolves through here and falls back to the bundled artwork.

export type LogoVariant = 'wide' | 'compact'
export type LogoTheme = 'light' | 'dark'

const BUNDLED: Record<LogoVariant, Record<LogoTheme, string>> = {
  wide: {
    light: '/media/logos/miel-flow-logo-light.svg',
    dark: '/media/logos/miel-flow-logo-dark.svg',
  },
  // The compact artwork is square-ish: the mobile header and the minimized
  // sidebar have height but no width to spend on a wordmark.
  compact: {
    light: '/media/logos/miel-flow-mark.svg',
    dark: '/media/logos/miel-flow-mark.svg',
  },
}

const CDN_FILE: Record<LogoVariant, string> = {
  wide: 'company/logo.png',
  compact: 'company/logo-2.png',
}

export const bundledLogo = (variant: LogoVariant, theme: LogoTheme = 'light'): string =>
  BUNDLED[variant][theme]

export const brandLogo = (
  cdnDomain: string | undefined,
  variant: LogoVariant,
  theme: LogoTheme = 'light'
): string => {
  const domain = (cdnDomain || '').trim().replace(/\/+$/, '')
  return domain ? `${domain}/${CDN_FILE[variant]}` : bundledLogo(variant, theme)
}

export const brandName = (companyName?: string): string => (companyName || '').trim() || 'MielFlow'

// A tenant logo that 404s leaves the alt text behind, which is what made the
// logo look like plain text. Swap the bundled artwork in on the first failure
// only — a failing fallback would otherwise loop.
export const logoErrorFallback =
  (fallback: string) =>
  (event: {currentTarget: HTMLImageElement}): void => {
    const img = event.currentTarget
    if (!img || img.dataset.logoFallback === 'true') {
      return
    }
    img.dataset.logoFallback = 'true'
    img.src = fallback
  }

// A print document is written into an iframe or a new tab whose own URL is
// `about:blank`, so a root-relative path there has nothing to resolve against.
// The CDN url is already absolute; the bundled fallback needs the app's origin
// glued on.
export const absoluteLogo = (src: string, origin?: string): string => {
  if (/^[a-z][a-z0-9+.-]*:/i.test(src)) {
    return src
  }
  const base = origin !== undefined ? origin : typeof window === 'undefined' ? '' : window.location.origin
  return `${base}${src}`
}

export const printLogo = (cdnDomain: string | undefined, origin?: string): string =>
  absoluteLogo(brandLogo(cdnDomain, 'wide'), origin)
